import { check } from "@tauri-apps/plugin-updater";
import { relaunch } from "@tauri-apps/plugin-process";
import { ElLoading, ElMessageBox } from "element-plus";
import { h } from "vue";
import { isTauri } from "@tauri-apps/api/core";
import { transformI18n, $t } from "@/plugins/i18n";

const formatReleaseNotes = (notes?: string) => {
  if (!notes) return [transformI18n($t("updater.noReleaseNotes"))];
  return notes
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .map(line => line.replace(/^[-*]\s*/, ""));
};

const formatUpdateDate = (date?: string) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString();
};
/**
 * 检查客户端更新
 */
export async function checkForUpdates() {
  // 不是 Tauri 环境直接返回
  if (!isTauri()) return;
  try {
    const update = await check();
    if (!update) {
      console.warn("更新检查返回 null，请检查服务器配置或没有新版本");
      return;
    }
    console.log(
      `found update ${update.version} from ${update.date} with notes ${update.body}`
    );
    const releaseNotes = formatReleaseNotes(update.body);
    const formattedDate = formatUpdateDate(update.date);
    ElMessageBox.confirm(
      h("div", { class: "update-dialog__content" }, [
        h("div", { class: "update-dialog__header" }, [
          h(
            "div",
            { class: "update-dialog__title" },
            transformI18n($t("updater.foundNewVersion"))
          ),
          h("div", { class: "update-dialog__badge" }, `v${update.version}`)
        ]),
        h("div", { class: "update-dialog__meta" }, [
          formattedDate
            ? h("span", { class: "update-dialog__meta-item" }, [
                transformI18n($t("updater.releaseDate")),
                h("b", formattedDate)
              ])
            : null
        ]),
        h(
          "div",
          { class: "update-dialog__notes-title" },
          transformI18n($t("updater.releaseNotes"))
        ),
        h(
          "ul",
          { class: "update-dialog__notes" },
          releaseNotes.map(note =>
            h("li", { class: "update-dialog__note" }, note)
          )
        )
      ]),
      transformI18n($t("updater.systemUpdate")),
      {
        confirmButtonText: transformI18n($t("updater.updateNow")),
        cancelButtonText: transformI18n($t("updater.updateLater")),
        customClass: "update-dialog",
        showCancelButton: true,
        closeOnClickModal: false
      }
    ).then(async () => {
      let downloaded = 0;
      let contentLength = 0;
      const loading = ElLoading.service({
        lock: true,
        text: transformI18n($t("updater.preparingDownload")),
        background: "rgba(0, 0, 0, 0.6)",
        customClass: "update-loading"
      });
      await update.downloadAndInstall(event => {
        switch (event.event) {
          case "Started":
            contentLength = event.data.contentLength;
            loading.setText(
              `${transformI18n($t("updater.downloading"))} 0%...`
            );
            console.log(
              `started downloading ${event.data.contentLength} bytes`
            );
            break;
          case "Progress":
            downloaded += event.data.chunkLength;
            if (contentLength > 0) {
              const percent = Math.min(
                100,
                Math.round((downloaded / contentLength) * 100)
              );
              loading.setText(
                `${transformI18n($t("updater.downloading"))} ${percent}%...`
              );
            }
            console.log(`downloaded ${downloaded} from ${contentLength}`);
            break;
          case "Finished":
            loading.setText(
              transformI18n($t("updater.downloadCompletedInstalling"))
            );
            console.log("download finished");
            break;
        }
      });
      loading.setText(transformI18n($t("updater.installCompletedRestarting")));
      loading.close();
      console.log("update installed");
      await relaunch();
    });
  } catch (error: any) {
    console.error("检查更新失败", error);
  }
}
